import { useState } from "react";
import { ChevronDown, ShieldCheck, LogOut, Code2 } from "lucide-react";

const SPEC_BLOCK = `{
  "system":          "Invisible System Design v3.2",
  "platforms":       ["iMessage", "WhatsApp", "Telegram", "Slack"],
  "routing":         "Unified ISD core, provider-agnostic webhooks",
  "latency_p50":     "142 ms",
  "latency_p99":     "390 ms",
  "uptime_sla":      "99.9% (43m 49s / month)",
  "encryption": {
    "in_transit":    "TLS 1.3",
    "at_rest":       "AES-256",
    "e2e":           "Where platform supports (iMessage, WhatsApp, Signal)"
  },
  "compliance":      ["SOC 2 Type II aligned", "GDPR-ready", "HIPAA on request"],
  "observability":   "OpenTelemetry · structured logs · PagerDuty",
  "deploy_targets":  ["AWS (primary)", "GCP (failover)"],
  "change_workflow": "email → triage → architect review → ship → digest",
  "human_in_loop":   "Every PR. Every release. Every incident.",
  "data_residency":  "US / EU / configurable",
  "exit_policy":     "Architecture handover + full export. No lock-in."
}`;

const SECURITY_ITEMS = [
  {
    title: "Secrets vaulting + IAM cleanup",
    body:
      "We migrate .env files into a real secrets manager, audit AWS/Stripe/API key ownership, and move customers off personal keys onto LLC-owned roles. Part of the $1,000 setup fee.",
  },
  {
    title: "Pre-commit guardrails",
    body:
      'Direct commits to main blocked at the local repo level. No more "oops I pushed straight to prod." Every change goes through review.',
  },
  {
    title: "Email authentication (SPF / DKIM / DMARC)",
    body:
      "Full domain authentication setup, progressive DMARC tightening (none → quarantine → reject), separate subdomains for transactional vs. cold outreach so reputation doesn't bleed. RFC 8058 one-click unsubscribe wired automatically.",
  },
  {
    title: "Deliverability monitoring",
    body:
      'Postmaster Tools (Gmail), SNDS (Outlook), aggregate DMARC reports, suppression-list reconciliation. Customers stop wondering "are my emails landing in spam."',
  },
  {
    title: "SSL/TLS automation",
    body: "Let's Encrypt cert provisioning + renewal, HTTPS-only enforcement.",
  },
  {
    title: "Firewall + intrusion protection",
    body:
      "CSF + fail2ban configured on managed hosts, SSH hardening, brute-force lockout, abuse-signal rate limiting.",
  },
  {
    title: "Backup + DR drill",
    body:
      'Verified offsite restore test as part of onboarding. Not "we hope it works" — we actually restore it.',
  },
  {
    title: "ADA / WCAG accessibility remediation",
    body:
      "Real capability. We're actively remediating a state government site (psc.ga.gov) to WCAG 2.1 AA. Color contrast, skip-nav, heading hierarchy, focus management — DOJ Title II is a 2027 deadline most SMBs don't know about yet.",
  },
  {
    title: "Stripe webhook signature verification + PCI-aware patterns",
    body:
      "No raw card data ever touches customer infra; signed webhooks, signing-secret rotation, per-module SDK auth.",
  },
  {
    title: "Dependency + drift monitoring",
    body:
      "Automated detection of uncommitted prod changes, stale branches, missing env vars, port conflicts — before deploy, not after.",
  },
];

const OFFBOARDING_ITEMS = [
  { k: "Full source code", v: "Delivered via GitHub transfer or a .zip archive." },
  { k: "Wiki.md", v: "Record of every lesson learned across the engagement." },
  { k: "Gameplan.md", v: "Project plan + the future-features backlog we'd queued for you." },
  { k: "Fix Guide", v: "Documented fixes for all known bugs — useful if you exit early." },
];

function Row({ id, eyebrow, title, icon: Icon, open, onToggle, testId, children }) {
  return (
    <div className="border-b border-white/10">
      <button
        type="button"
        onClick={onToggle}
        data-testid={testId}
        className="w-full flex items-center justify-between gap-6 py-6 group hover:border-white/30 transition-colors"
      >
        <div className="flex items-center gap-4 text-left">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            {eyebrow}
          </span>
          <Icon className="w-5 h-5 text-yellow-400 hidden sm:block" strokeWidth={1.5} />
          <span className="font-heading text-xl sm:text-2xl md:text-3xl font-light tracking-tight text-white group-hover:text-yellow-400 transition-colors">
            {title}
          </span>
        </div>
        <ChevronDown
          className={`w-6 h-6 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>

      {open && (
        <div data-testid={`${testId}-content`} className="pb-12 pt-2 fade-up">
          {children}
        </div>
      )}
    </div>
  );
}

export default function TechSpecs() {
  const [openId, setOpenId] = useState(null);
  const toggle = (id) => setOpenId((curr) => (curr === id ? null : id));

  return (
    <section
      data-testid="tech-specs-section"
      className="relative py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Row 1 — Technical Specifications */}
        <Row
          id="specs"
          eyebrow="§10"
          icon={Code2}
          title="Technical Specifications Appendix"
          open={openId === "specs"}
          onToggle={() => toggle("specs")}
          testId="tech-specs-toggle"
        >
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4 space-y-4">
              <p className="text-base leading-relaxed text-zinc-400 font-light">
                For the engineers in the room. A condensed view of the system
                you're buying into. Full architecture documents are shared
                under NDA after the consultation.
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-600">
                last updated · dec 2025
              </p>
            </div>

            <div className="lg:col-span-8">
              <div className="border border-white/10 bg-black">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500/70" />
                    <span className="w-2 h-2 bg-yellow-400/70" />
                    <span className="w-2 h-2 bg-green-500/70" />
                  </div>
                  <span className="font-mono text-[11px] text-zinc-500 tracking-tight">
                    isd-system.json
                  </span>
                </div>
                <pre
                  data-testid="tech-specs-block"
                  className="font-mono text-[12px] md:text-[13px] leading-relaxed text-zinc-300 p-6 overflow-x-auto whitespace-pre"
                >
                  {SPEC_BLOCK}
                </pre>
              </div>
            </div>
          </div>
        </Row>

        {/* Row 2 — Security & Compliance */}
        <Row
          id="security"
          eyebrow="§11"
          icon={ShieldCheck}
          title="Security and Compliance"
          open={openId === "security"}
          onToggle={() => toggle("security")}
          testId="security-toggle"
        >
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="text-base leading-relaxed text-zinc-400 font-light">
                Real controls, not checkbox theatre. Most of this lands on day
                one as part of onboarding. The rest is part of how we operate
                every system we touch.
              </p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-yellow-400">
                Included with every plan
              </p>
            </div>

            <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {SECURITY_ITEMS.map((item, i) => (
                <li
                  key={item.title}
                  data-testid={`security-item-${i}`}
                  className="bg-[#050505] p-6 hover:bg-[#0a0a0a] transition-colors"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-yellow-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-heading text-base sm:text-lg font-light tracking-tight text-white leading-snug">
                      {item.title}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-400 font-light leading-relaxed">
                    {item.body}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </Row>

        {/* Row 3 — Offboarding */}
        <Row
          id="offboarding"
          eyebrow="§12"
          icon={LogOut}
          title="Offboarding"
          open={openId === "offboarding"}
          onToggle={() => toggle("offboarding")}
          testId="offboarding-toggle"
        >
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-base sm:text-lg leading-relaxed text-zinc-300 font-light">
                We hope you stay forever.{" "}
                <span className="text-zinc-500">
                  But if you need to go, we make it totally easy.
                </span>
              </p>
              <p className="mt-6 text-base leading-relaxed text-zinc-400 font-light">
                Your exit package includes everything you need to keep the
                system running, hand it to another team, or pause it
                cleanly — no hostage data, no exit fees.
              </p>
            </div>

            <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {OFFBOARDING_ITEMS.map((item, i) => (
                <li
                  key={item.k}
                  data-testid={`offboarding-item-${i}`}
                  className="bg-[#050505] p-6 hover:bg-[#0a0a0a] transition-colors"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-yellow-400 mb-2">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-heading text-lg font-light tracking-tight text-white mb-2">
                    {item.k}
                  </div>
                  <p className="text-sm text-zinc-400 font-light leading-relaxed">
                    {item.v}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </Row>
      </div>
    </section>
  );
}
